import React, {useRef, useEffect, useReducer, createContext, useContext, memo} from "react";
import parse from "html-react-parser";
import {Radio, Checkbox, Input} from "antd";
import {QuestionAnswerResultBlock} from "../Question";

const InputContext = createContext({});

const ACTIONS = {
    HYDRATE_DATA: "HYDRATE_DATA",
    UPDATE_INPUT_VALUE: "UPDATE_INPUT_VALUE",
    SET_RESULTS_DATA: "SET_RESULTS_DATA",
    SET_ERROR: "SET_ERROR",
}

const genQuestionInitialState = {
    dataHydrated: false,
    inputs: null,
    error: null,
}

function genQuestionReducer(state, action) {
    if (action.type === ACTIONS.HYDRATE_DATA) {
        return {...state, ...genQuestionInitialState, ...action.payload, dataHydrated: true};
    } else if (action.type === ACTIONS.UPDATE_INPUT_VALUE) {
        return {
            ...state,
            inputs: {
                ...state.inputs,
                [action.payload.name]: {
                    ...state.inputs[action.payload.name],
                    value: action.payload.value
                }
            }
        };
    } else if (action.type === ACTIONS.SET_RESULTS_DATA) {
        const newInputs = {...state.inputs};
        Object.keys(action.payload).forEach(name => {
            if (newInputs[name]) {
                newInputs[name] = {...newInputs[name], ...action.payload[name]};
            }
        });
        return {...state, inputs: newInputs};
    } else if (action.type === ACTIONS.SET_ERROR) {
        return {...state, error: action.payload, dataHydrated: true};
    } else {
        return state;
    }
}

const GenericQuestion = props => {
    const {question, result, displayAnswerOnResult, readOnly, onLoad, onError, updateQuestionData} = props;
    const [state, dispatch] = useReducer(genQuestionReducer, genQuestionInitialState);
    const inputSelectors = "input, select, textarea";
    const staticContentRef = useRef(null);
    const questionRef = useRef();

    useEffect(() => {
        // console.log("state:", state.inputs);
    }, [state]);

    useEffect(() => {
        onError?.(state.error);
    }, [state.error]);

    useEffect(() => {
        let hasError = false;
        const html = question?.formattedInstructions || question?.instructions || '';
        if (!html || html === "No question content") return;

        const parser = new DOMParser();
        const doc= parser.parseFromString(html, 'text/html');
        const inputElements = doc.querySelectorAll(inputSelectors);

        if (inputElements.length === 0) {
            hasError = true;
            dispatch({type: ACTIONS.SET_ERROR, payload: "Question content error, no inputs found."});
            return;
        }

        const inputs = {};
        const groupMap = {};

        inputElements.forEach(el => {
            const name = el.getAttribute('name');
            if (!name) return;
            groupMap[name] = (groupMap[name] || 0) + 1;
        });

        Object.entries(groupMap).forEach(([name, count]) => {
            const [, questionId, index] = name.match(/^(\d+)\[(\d+)\]$/);
            inputs[name] = {
                name: name,
                index: parseInt(index),
            };

            if (count > 1) {
                const inputEls = doc.querySelectorAll(`input[name="${name}"]`);

                inputs[name].type ??= inputEls[0].type;

                const options = [];
                for (const inputEl of inputEls) {
                    let containerNode = inputEl.parentElement;
                    while (containerNode && containerNode.parentElement) {
                        if (containerNode.tagName === 'LI' || containerNode.tagName === 'TR') {
                            break;
                        }
                        if (containerNode.parentElement.tagName === 'BODY') {
                            break;
                        }
                        if (containerNode.parentElement.querySelectorAll(`input[name="${name}"]`).length > 1) {
                            break;
                        }
                        containerNode = containerNode.parentElement;
                    }
                    containerNode.classList.add(`ch-q-gen-choice-blk`);

                    options.push({
                        id: inputEl.getAttribute('id'),
                        value: inputEl.value,
                        marker: inputEl.value.replace(`${inputs[name].type}-`, ''),
                    });
                }
                inputs[name].options = options;
            } else {
                const inputEl = doc.querySelector(`input[name="${name}"]`);
                const styleAttr = inputEl.getAttribute('style') || '';
                const styleMatch = styleAttr.match(/width\s*:\s*([^;]+)/);
                const styleWidth = styleMatch ? styleMatch[1].trim() : null;

                inputs[name].id ??= inputEl.getAttribute('id');
                inputs[name].type ??= inputEl.type;
                inputs[name].maxLength ??= inputEl.maxLength;
                inputs[name].styleWidth ??= styleWidth;
                inputEl.setAttribute('data-q-input-root', name);
            }
        });

        if (Object.keys(inputs).length === 0) {
            hasError = true;
            dispatch({type: ACTIONS.SET_ERROR, payload: "Question content error, unable to parse question."});
            return;
        }

        const renderedInputs = new Set();
        staticContentRef.current = parse(doc.body.innerHTML, {
            replace: (domNode) => {
                const rootName = domNode.attribs?.['data-q-input-root'];
                if (rootName) {
                    if (!renderedInputs.has(rootName)) {
                        renderedInputs.add(rootName);
                        return <ControlledInput key={rootName} name={rootName} />;
                    } else {
                        return <React.Fragment />;
                    }
                }
                // for radio and checkboxes, may switch to ControlledInput when a safer parse of options can be done.
                const name = domNode.attribs?.name;
                if (name && groupMap[name] > 1) {
                    return (
                        <SingleControlledInput name={name} value={domNode.attribs?.value} />
                    )
                }
            }
        });

        if (hasError) return;

        dispatch({type: ACTIONS.HYDRATE_DATA, payload: {inputs}});
    }, [question]);

    useEffect(() => {
        if (!result || !state.dataHydrated || !state.inputs) return;

        const rawUserValues = result.userInput?.split('|') || [];
        const rawCorrectChunks = question.correctAnswer?.split('¦') || [];
        const resultsPayload = {};

        Object.values(state.inputs).forEach((inputObj) => {
            const userValue = rawUserValues[inputObj.index];
            const correctChunk = rawCorrectChunks[inputObj.index] || "";

            const correctAnswers = correctChunk.split('|')
                .map(v => v.trim())
                .filter(v => v !== "");

            const inputUpdate = {
                correctAnswers: correctAnswers
            };

            if (inputObj.options) {
                correctAnswers.forEach(marker => {
                    const selector = `[data-name="${inputObj.name}"][data-marker="${marker}"]`;
                    const inputEl = questionRef.current.querySelector(selector);
                    inputEl?.closest('.ch-q-gen-choice-blk').classList.add('ch-q-gen-correct');
                });
            }

            if (userValue !== undefined && userValue !== "") {
                if (inputObj.type === 'radio') {
                    inputUpdate.value = `${inputObj.type}-${userValue}`;
                } else if (inputObj.type === 'checkbox') {
                    inputUpdate.value = userValue.split(',').map(val => `${inputObj.type}-${val}`);
                } else {
                    inputUpdate.value = userValue;
                }
            }

            resultsPayload[inputObj.name] = inputUpdate;
        });

        dispatch({type: ACTIONS.SET_RESULTS_DATA, payload: resultsPayload});
    }, [question, result, state.dataHydrated]);

    useEffect(() => {
        if (!state.dataHydrated || !state.inputs || readOnly || result) return;

        let isSubmittable = true;
        const rawData = Object.values(state.inputs).map(inputObj => {
            if (!inputObj.value || (Array.isArray(inputObj.value) && inputObj.value.length === 0)) {
                isSubmittable = false;
            }
            return { name: inputObj.name, value: inputObj.value };
        });

        updateQuestionData?.(rawData, isSubmittable);
    }, [state.dataHydrated, state.inputs, readOnly, result]);

    useEffect(() => {
        if (!state.dataHydrated || !questionRef.current) return;

        if (window.MathJax?.typesetPromise) {
            window.MathJax.typesetPromise([questionRef.current])
                .catch(() => dispatch({type: ACTIONS.SET_ERROR, payload: "MathJax failed to load."}))
                .finally(() => handleInitialFocus());
        } else {
            handleInitialFocus();
        }

        function handleInitialFocus() {
            onLoad?.(state.inputs);
            if (readOnly) return;

            setTimeout(() => {
                questionRef.current?.focus({preventScroll: true});
            }, 50);
        }
    }, [state.dataHydrated, readOnly]);

    if (state.error) return <></>

    return (
        <div
            ref={questionRef}
            tabIndex={-1}
            className="ch-q-gen"
        >
            <InputContext.Provider value={{
                inputs: state.inputs,
                result: result,
                readOnly: readOnly,
                dispatch: dispatch,
            }}>
                {staticContentRef.current}
                {result && displayAnswerOnResult && (
                    <QuestionAnswerResultBlock
                        result={result}
                        questionAnswerType={question.questionAnswerType}
                    />
                )}
            </InputContext.Provider>
        </div>
    )
}

const ControlledInput = memo(({name}) => {
    const {inputs, result, readOnly, dispatch} = useContext(InputContext);
    const inputObj = inputs?.[name];

    if (!inputObj) return null;

    const commonProps = {
        "data-input-index": inputObj.index,
        disabled: readOnly || result,
        value: inputObj.value !== undefined ? inputObj.value : (inputObj.type === 'checkbox' ? [] : ''),
        onChange: (event) => {
            const newValue = event?.target ? event.target.value : event;
            dispatch({type: ACTIONS.UPDATE_INPUT_VALUE, payload: {name, value: newValue}});
        }
    };

    if (inputObj.type === 'radio' || inputObj.type === 'checkbox') {
        const CheckComponent = inputObj.type === 'radio' ? Radio.Group : Checkbox.Group;
        const stylizedOptions = inputObj.options.map(opt => {
            const isCorrect = inputObj.correctAnswers && inputObj.correctAnswers?.includes(opt.marker);
            return {
                ...opt,
                id: opt.id,
                label: (
                    <>
                        {parse(opt.label)}
                        {isCorrect && result && (
                            <span className="ch-sr-only"> (Correct Answer)</span>
                        )}
                    </>
                ),
                className: isCorrect ? "ch-q-gen-correct" : ""
            };
        });
        return (
            <CheckComponent
                {...commonProps}
                className="ch-q-gen-choice"
                style={{'--list-style-type': inputObj.listType || 'lower-alpha'}}
                vertical="true"
                options={stylizedOptions}
            />
        )
    } else {
        let width;
        const defaultWidth = 24;
        const inputPadding = 12;
        if (inputObj.styleWidth) {
            const numericWidth = parseFloat(inputObj.styleWidth) ?? defaultWidth;
            width = `${numericWidth + inputPadding}px`;
        } else {
            width = inputObj.maxLength ? `${inputObj.maxLength + 2}ch` : `${24 + inputPadding}px`;
        }
        return (
            <Input
                {...commonProps}
                id={inputObj.id}
                className="ch-q-gen-text"
                style={{width: width}}
                {...(inputObj.maxLength && {maxLength: inputObj.maxLength})}
            />
        )
    }
});

// this is for checkbox and radios only, when they are not in .Group format.
const SingleControlledInput = memo(({name, value}) => {
    const {inputs, result, readOnly, dispatch} = useContext(InputContext);
    const inputObj = inputs?.[name];
    if (!inputObj) return null;

    const option = inputObj.options.find(option => option.value === value);
    if (!option) return null;

    const isCheckbox = inputObj.type === 'checkbox';
    const CheckComponent = isCheckbox ? Checkbox : Radio;

    return (
        <CheckComponent
            className="ch-q-gen-choice-s"
            id={option.id}
            data-name={name}
            data-input-index={inputObj.index}
            data-marker={option.marker}
            disabled={readOnly || result}
            checked={
                isCheckbox
                    ? Array.isArray(inputObj.value) && inputObj.value.includes(option.value)
                    : inputObj.value === option.value
            }
            onChange={(event) => {
                const isChecked = event.target.checked;
                let newValue;

                if (isCheckbox) {
                    const currentArray = Array.isArray(inputObj.value) ? inputObj.value : [];
                    if (isChecked) {
                        newValue = [...currentArray, option.value];
                    } else {
                        newValue = currentArray.filter(value => value !== option.value);
                    }
                } else {
                    newValue = option.value;
                }
                dispatch({type: ACTIONS.UPDATE_INPUT_VALUE, payload: {name, value: newValue}});
            }}
        />
    );
})

export default GenericQuestion;